<template>
  <div class="uh-node">
    <div
      class="uh-row"
      :class="rowClass"
      role="row"
      :aria-level="level + 1"
    >
      <div
        class="uh-row-grid"
        :style="{ paddingLeft: indent + 'px' }"
      >
        <div class="uh-cell-name">
          <button
            v-if="hasChildren"
            type="button"
            class="uh-chevron"
            :aria-expanded="expanded ? 'true' : 'false'"
            :aria-label="(expanded ? 'Recolher ' : 'Expandir ') + node.name"
            @click="$emit('toggle', node.id)"
          >
            <svg
              class="uh-chevron-icon"
              :class="{ 'is-open': expanded }"
              viewBox="0 0 12 12"
              fill="none"
            >
              <path
                d="M3 2 L8 6 L3 10"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
          <span
            v-else
            class="uh-chevron uh-chevron--spacer"
          />

          <span
            class="uh-icon"
            :class="iconClass"
          >
            <svg
              v-if="node.isTeam"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                cx="8.5"
                cy="8"
                r="2.6"
                stroke="currentColor"
                stroke-width="1.6"
              />
              <path
                d="M3.5 18c0-2.8 2.2-4.8 5-4.8s5 2 5 4.8"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
              />
              <circle
                cx="16"
                cy="8.3"
                r="2.1"
                stroke="currentColor"
                stroke-width="1.6"
              />
              <path
                d="M14.8 13.6c2.4.2 4.2 2.1 4.2 4.4"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
              />
            </svg>
            <svg
              v-else
              viewBox="0 0 24 24"
              fill="none"
            >
              <rect
                x="4.5"
                y="3.5"
                width="11"
                height="17"
                rx="1"
                stroke="currentColor"
                stroke-width="1.6"
              />
              <path
                d="M15.5 9H19a1 1 0 0 1 1 1v9.5a1 1 0 0 1-1 1h-3.5"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linejoin="round"
              />
              <path
                d="M7.5 7h1.5M11 7h1.5M7.5 10.5h1.5M11 10.5h1.5M7.5 14h1.5M11 14h1.5"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
              />
              <path
                d="M8 20.5V17.5h3v3"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linejoin="round"
              />
            </svg>
          </span>

          <span class="uh-texts">
            <span class="uh-name">{{ node.name }}</span>
            <span class="uh-sub">{{ subtitle }}</span>
          </span>
        </div>

        <div class="uh-cell-type">
          <span
            class="uh-badge"
            :class="badgeClass"
          >{{ typeLabel }}</span>
        </div>

        <div
          v-if="canManage"
          class="uh-cell-actions"
        >
          <button
            type="button"
            class="uh-action uh-action--edit"
            :aria-label="'Editar ' + typeLabel + ' ' + node.name"
            @click="$emit('edit', node)"
          >
            <lila-svg-icon
              :src="editIcon"
              :prevent="false"
            />
          </button>
          <button
            type="button"
            class="uh-action uh-action--delete"
            :aria-label="'Excluir ' + typeLabel + ' ' + node.name"
            @click="$emit('delete', node)"
          >
            <lila-svg-icon
              :src="deleteIcon"
              :prevent="false"
            />
          </button>
        </div>
      </div>
    </div>

    <template v-if="hasChildren && expanded">
      <units-hierarchy-row
        v-for="child in node.children"
        :key="child.id"
        :node="child"
        :level="level + 1"
        :is-expanded="isExpanded"
        :can-manage="canManage"
        @toggle="$emit('toggle', $event)"
        @edit="$emit('edit', $event)"
        @delete="$emit('delete', $event)"
      />
    </template>
  </div>
</template>

<script>
import SvgIcon from "@components/shared/images/SvgIcon.vue";
import { childrenSummary } from "@/js/teamHierarchy";

export default {
  name: "UnitsHierarchyRow",
  components: {
    "lila-svg-icon": SvgIcon
  },
  props: {
    node: { type: Object, required: true },
    level: { type: Number, default: 0 },
    isExpanded: { type: Function, required: true },
    canManage: { type: Boolean, default: false }
  },
  emits: ["toggle", "edit", "delete"],
  data() {
    return {
      editIcon: require("@assets/icons/icon_editar.svg"),
      deleteIcon: require("@assets/icons/delete.svg")
    };
  },
  computed: {
    hasChildren() {
      return this.node.children.length > 0;
    },
    expanded() {
      return this.isExpanded(this.node.id);
    },
    indent() {
      return 16 + this.level * 40;
    },
    isRootUnit() {
      return !this.node.isTeam && this.level === 0;
    },
    isSubUnit() {
      return !this.node.isTeam && this.level > 0;
    },
    rowClass() {
      if (this.node.isTeam) return "uh-row--team";
      return this.isRootUnit ? "uh-row--root" : "uh-row--sub";
    },
    iconClass() {
      return this.node.isTeam ? "uh-icon--team" : "uh-icon--unit";
    },
    typeLabel() {
      if (this.node.isTeam) return "Equipe";
      return this.isRootUnit ? "Unidade" : "Subunidade";
    },
    badgeClass() {
      return this.node.isTeam ? "uh-badge--team" : "uh-badge--unit";
    },
    subtitle() {
      if (this.node.isTeam) {
        return this.level === 0 ? "Sem unidade vinculada" : "";
      }
      return childrenSummary(this.node);
    }
  }
};
</script>

<style scoped>
.uh-node + .uh-node {
  border-top: 1px solid #efedf2;
}

.uh-row-grid {
  display: grid;
  grid-template-columns: 1fr 130px 90px;
  align-items: center;
  padding-top: 10px;
  padding-bottom: 10px;
  padding-right: 20px;
  min-height: 60px;
  gap: 8px;
}

.uh-row--root .uh-row-grid {
  background: #f1eef8;
  min-height: 72px;
}

.uh-row--sub .uh-row-grid {
  background: #f8f7fb;
  min-height: 64px;
}

.uh-row--team .uh-row-grid {
  background: #ffffff;
  min-height: 60px;
}

.uh-cell-name {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.uh-chevron {
  flex: none;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #4b3a8f;
  padding: 0;
  border-radius: 6px;
}

.uh-chevron:hover {
  background: rgba(75, 58, 143, 0.08);
}

.uh-chevron--spacer {
  cursor: default;
}

.uh-chevron-icon {
  width: 12px;
  height: 12px;
  transform: rotate(0deg);
  transition: transform 0.15s ease;
}

.uh-chevron-icon.is-open {
  transform: rotate(90deg);
}

.uh-icon {
  flex: none;
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.uh-icon svg {
  width: 20px;
  height: 20px;
}

.uh-icon--unit {
  border-radius: 8px;
}

.uh-row--root .uh-icon--unit {
  background: #4b3a8f;
  color: #ffffff;
}

.uh-row--sub .uh-icon--unit {
  background: #e6e0f3;
  color: #4b3a8f;
}

.uh-icon--team {
  border-radius: 50%;
  background: #f4e6f0;
  color: #93307f;
}

.uh-texts {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.uh-name {
  font-size: 15px;
  color: #2e2450;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.uh-row--team .uh-name {
  font-weight: 400;
  color: #4a3050;
}

.uh-sub {
  font-size: 12px;
  color: #8a7f96;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.uh-cell-type {
  display: flex;
  justify-content: flex-start;
}

.uh-badge {
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.uh-badge--unit {
  background: #4b3a8f;
  color: #ffffff;
}

.uh-badge--team {
  background: #f4e6f0;
  color: #93307f;
}

.uh-cell-actions {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.uh-action {
  border: none;
  background: transparent;
  cursor: pointer;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  padding: 0;
}

.uh-action :deep(.svg-icon-component) {
  pointer-events: none;
  display: inline-flex;
}

.uh-action :deep(.svg-icon-component *) {
  pointer-events: none;
}

.uh-action--edit {
  color: #93307f;
}

.uh-action--edit :deep(svg) {
  width: 14px;
  height: 14px;
}

.uh-action--delete {
  color: #d9383a;
}

.uh-action--delete :deep(svg) {
  width: 14px;
  height: 14px;
}
</style>
